import {
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  HandlingFeeItem,
  UpdateHandlingFeeRequest,
  handlingFeesApi,
} from '@texas/api/endpoints/metadata/handlingFeesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';

interface HandlingFeeModalProps {
  handlingFee: HandlingFeeItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function HandlingFeeModal({
  handlingFee,
  isOpen,
  onClose,
  onSaved,
}: HandlingFeeModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request, loading, error } = useApiRequest(handlingFeesApi.update);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<UpdateHandlingFeeRequest>();

  useEffect(() => {
    if (handlingFee) reset(handlingFee);
  }, [handlingFee, reset]);

  if (!handlingFee) return null;

  const submit = async (data: UpdateHandlingFeeRequest) => {
    const response = await request(handlingFee.id, data);
    if (response.hasError) return;
    toast({
      title:
        data.archived !== handlingFee.archived
          ? t(
              data.archived
                ? 'general.entityArchived'
                : 'general.entityRestored',
              { name: data.name },
            )
          : t('general.updated'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  return (
    <Modal variant="entityDirectory" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('general.edit')}</ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={error} />
            <Flex direction="column" gap="4">
              <FormControl isRequired={true} isInvalid={!!errors.name}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', { required: true, maxLength: 100 })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.value}>
                <TexasFormLabel>{t('general.value')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  {...register('value', {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
                <ErrorLabel text={errors.value?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.displayOrder}>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  {...register('displayOrder', {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
                <ErrorLabel text={errors.displayOrder?.message} />
              </FormControl>
              <Checkbox {...register('archived')}>
                {t('general.archived')}
              </Checkbox>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={loading}>{t('general.save')}</SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
